/**
 * Feature Flag Management
 *
 * USAGE:
 * - loadFeatureFlag(flagName) - Load a flag and store in Vuex
 * - Access flags via shared global 'featureFlags' store
 *
 * PERFORMANCE:
 * - Batches multiple flag requests into single API calls
 * - Stores flags in reactive Vuex store for immediate access
 */

import Vue from 'vue';
import FeatureFlagsApi from '~/api/feature_flags';
import { registerModule } from '~/services/store_service';
import store from '~/base_store';

// May need to adjust based on usage,
// Increase if we find that some page-load requests are not being batched.
const BATCH_DELAY_MS = 100;

// ========================================
// Internal State
// ========================================
let batchedRequests = [];
let batchTimer = null;

// ========================================
// Vuex Store Setup
// ========================================
registerModule('featureFlags', {
    namespaced: true,
    state: {},
    mutations: {
        setFlag(state, { name, value }) {
            Vue.set(state, name, value);
        },
        setFlags(state, flags) {
            Object.keys(flags).forEach((key) => {
                Vue.set(state, key, flags[key]);
            });
        },
    },
    getters: {
        getFlag: (state) => (flagName) => state[flagName] || false,
        hasFlag: (state) => (flagName) => (
            Object.prototype.hasOwnProperty.call(state, flagName)
        ),
    },
});

// ========================================
// Batching System
// ========================================
function processBatchedRequests() {
    if (batchedRequests.length === 0) return;

    // Extract all unique flag names
    const allRequestedFlags = [...new Set(batchedRequests)];

    // Reset batch state
    batchedRequests = [];
    batchTimer = null;

    // Make single API call for all requested flags
    FeatureFlagsApi.getFeatureFlags({ requestedFlags: allRequestedFlags })
        .then((flagsFromApi) => {
            // Store all received flags in Vuex store
            Object.entries(flagsFromApi).forEach(([flagName, flagValue]) => {
                store.commit('featureFlags/setFlag', { name: flagName, value: flagValue });
            });
        })
        .catch(() => {
            // Silently handle API errors
        });
}

function addToBatchQueue(flagNames) {
    const normalizedFlags = Array.isArray(flagNames) ? flagNames : [flagNames];
    batchedRequests.push(...normalizedFlags);

    // Reset timer and schedule batch processing
    if (batchTimer) {
        clearTimeout(batchTimer);
    }
    batchTimer = setTimeout(processBatchedRequests, BATCH_DELAY_MS);
}

function checkStore(flagName) {
    if (store.getters['featureFlags/hasFlag'](flagName)) {
        return store.getters['featureFlags/getFlag'](flagName);
    }
    return null;
}

// ========================================
// Public API
// ========================================
function loadFeatureFlag(flagName) {
    const storeValue = checkStore(flagName);
    if (storeValue !== null) {
        return; // Already loaded, nothing to do
    }

    addToBatchQueue([flagName]);
}

export default loadFeatureFlag;
